import { type ExtendedFetcher } from '../atlassian-client-types';

import {
  GraphQLNetworkError,
  GraphQLRetryExhaustedError,
  isRetryableError,
} from './errors';
import {
  type GraphQLRequestBody,
  type GraphQLResponseBody,
  type RequestContext,
  type RetryConfig,
} from './types';

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_INITIAL_DELAY = 200;
const DEFAULT_MAX_DELAY = 1000;

/**
 * HTTP transport layer for GraphQL requests.
 * Handles retry logic, header injection, and request execution.
 */
export class GraphQLTransport {
  private readonly fetcher: ExtendedFetcher | typeof fetch;
  private readonly baseHeaders: Record<string, string>;
  private readonly retryConfig: Required<RetryConfig>;
  private readonly useRetry: boolean;

  constructor(options: {
    fetcher?: ExtendedFetcher;
    headers?: Record<string, string>;
    retry?: RetryConfig;
  }) {
    this.fetcher = options.fetcher ?? fetch.bind(globalThis);
    this.baseHeaders = options.headers ?? {};
    this.retryConfig = {
      maxAttempts: options.retry?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
      initialDelay: options.retry?.initialDelay ?? DEFAULT_INITIAL_DELAY,
      maxDelay: options.retry?.maxDelay ?? DEFAULT_MAX_DELAY,
      jitter: options.retry?.jitter ?? true,
    };
    this.useRetry = !options.fetcher;
  }

  /**
   * Execute a GraphQL request with retry logic.
   */
  async execute<T>(
    endpoint: string,
    body: GraphQLRequestBody,
    context?: RequestContext,
  ): Promise<GraphQLResponseBody<T>> {
    const headers = this.buildHeaders(context);
    const requestInit: RequestInit = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: context?.signal,
      ...context?.fetchOptions,
    };

    if (this.useRetry) {
      return this.executeWithRetry<T>(endpoint, requestInit);
    }

    return this.executeSingle<T>(endpoint, requestInit);
  }

  private async executeWithRetry<T>(
    endpoint: string,
    init: RequestInit,
  ): Promise<GraphQLResponseBody<T>> {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt < this.retryConfig.maxAttempts; attempt++) {
      try {
        return await this.executeSingle<T>(endpoint, init);
      } catch (error) {
        lastError = error as Error;

        if (!isRetryableError(error) || attempt >= this.retryConfig.maxAttempts - 1) {
          throw error;
        }

        await this.delay(attempt);
      }
    }

    throw new GraphQLRetryExhaustedError(
      `GraphQL request failed after ${this.retryConfig.maxAttempts} attempts: ${lastError?.message}`,
      this.retryConfig.maxAttempts,
      { cause: lastError },
    );
  }

  private async executeSingle<T>(
    endpoint: string,
    init: RequestInit,
  ): Promise<GraphQLResponseBody<T>> {
    let response: Response;

    try {
      response = await (this.fetcher as typeof fetch)(endpoint, init);
    } catch (error) {
      throw new GraphQLNetworkError(`Network error: ${(error as Error).message}`, {
        cause: error as Error,
      });
    }

    if (!response.ok) {
      throw new GraphQLNetworkError(`HTTP ${response.status}: ${response.statusText}`, {
        statusCode: response.status,
        response,
      });
    }

    try {
      const json = (await response.json()) as GraphQLResponseBody<T>;
      return json;
    } catch {
      throw new GraphQLNetworkError('Failed to parse JSON response', {
        statusCode: response.status,
        response,
      });
    }
  }

  private buildHeaders(context?: RequestContext): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      ...this.baseHeaders,
      ...context?.headers,
    };
  }

  private delay(attempt: number): Promise<void> {
    const delay = this.calculateBackoff(attempt);
    return new Promise((resolve) => setTimeout(resolve, delay));
  }

  private calculateBackoff(attempt: number): number {
    const delay = Math.min(
      this.retryConfig.initialDelay * Math.pow(2, attempt),
      this.retryConfig.maxDelay,
    );

    if (this.retryConfig.jitter) {
      return delay + Math.random() * delay * 0.3;
    }

    return delay;
  }
}
